import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { paymentApi } from '@formosa/shared/api/payment';
import { ApiError } from '@formosa/shared/api/request';
import { maskWalletAddress } from '../../constants/withdrawConstants';
import { useTranslation } from '../../i18n/LocaleContext';

const VERIFY_SCENE = 'DELETE_WITHDRAW_ADDRESS';

export default function WithdrawUnbindPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const address = location.state?.address;

  const [member, setMember] = useState(null);
  const [verifyTab, setVerifyTab] = useState('email');
  const [verifyCode, setVerifyCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const hasEmail = Boolean(member?.email?.trim());
  const hasMobile = Boolean(member?.mobile?.trim());

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (hasEmail) {
      tabs.push('email');
    }
    if (hasMobile) {
      tabs.push('phone');
    }
    return tabs;
  }, [hasEmail, hasMobile]);

  useEffect(() => {
    if (!address?.channelType) {
      navigate('/withdraw/addresses', { replace: true });
      return;
    }
    let cancelled = false;
    memberApi
      .getInfo()
      .then((info) => {
        if (!cancelled) {
          setMember(info || null);
          if (info?.email?.trim()) {
            setVerifyTab('email');
          } else if (info?.mobile?.trim()) {
            setVerifyTab('phone');
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMember(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [address, navigate]);

  if (!address?.channelType) {
    return null;
  }

  async function handleSendCode() {
    setError('');
    setSendingCode(true);
    try {
      if (verifyTab === 'email') {
        if (!member?.email?.trim()) {
          throw new ApiError(t('withdraw.unbindEmailMissing'));
        }
        await memberApi.sendVerifyCodeEmail({
          scene: VERIFY_SCENE,
          email: member.email.trim(),
        });
      } else {
        if (!member?.mobile?.trim()) {
          throw new ApiError(t('withdraw.unbindPhoneMissing'));
        }
        await memberApi.sendVerifyCodePhone({
          scene: VERIFY_SCENE,
          countryEnName: member.countryEnName || 'TW',
          phoneAreaCode: member.areaCode || member.phoneAreaCode || '+886',
          phoneNumber: member.mobile.trim(),
        });
      }
      setCodeSent(verifyTab);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('withdraw.unbindCodeSendFailed'));
    } finally {
      setSendingCode(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!verifyCode.trim()) {
      setError(t('withdraw.unbindCodeRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await paymentApi.unbindWithdrawAddress({
        channelType: address.channelType,
        verifyCode: verifyCode.trim(),
      });
      navigate(location.state?.from || '/withdraw/addresses', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('withdraw.unbindFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="deposit-page withdraw-unbind-page">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('withdraw.unbindPageTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      <div className="withdraw-unbind-page__summary">
        <strong>{address.channelType}</strong>
        <span>{maskWalletAddress(address.walletAddress)}</span>
      </div>

      {availableTabs.length > 1 && (
        <div className="withdraw-unbind-page__tabs">
          {hasEmail && (
            <button
              type="button"
              className={verifyTab === 'email' ? 'is-active' : ''}
              onClick={() => setVerifyTab('email')}
            >
              {t('withdraw.unbindTabEmail')}
            </button>
          )}
          {hasMobile && (
            <button
              type="button"
              className={verifyTab === 'phone' ? 'is-active' : ''}
              onClick={() => setVerifyTab('phone')}
            >
              {t('withdraw.unbindTabPhone')}
            </button>
          )}
        </div>
      )}

      <form className="withdraw-unbind-page__form" onSubmit={handleSubmit}>
        <div className="withdraw-field">
          <label className="withdraw-field__label" htmlFor="unbind-verify-code">
            {t('withdraw.unbindVerifyCode')}
          </label>
          <div className="withdraw-unbind-page__code-row">
            <input
              id="unbind-verify-code"
              className="withdraw-field__select"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              placeholder={t('withdraw.unbindVerifyCodePlaceholder')}
            />
            <button
              type="button"
              className="withdraw-unbind-page__send-btn"
              disabled={sendingCode}
              onClick={handleSendCode}
            >
              {sendingCode ? t('common.loading') : t('withdraw.unbindSendCode')}
            </button>
          </div>
          {codeSent && <p className="withdraw-unbind-page__sent">{t(codeSent === 'email' ? 'emailVerification.accepted' : 'withdraw.unbindCodeSent')}</p>}
        </div>

        {error && <p className="deposit-page__error">{error}</p>}

        <button
          type="submit"
          className={`withdraw-page__submit ${verifyCode.trim() ? 'is-ready' : ''}`}
          disabled={submitting || !verifyCode.trim()}
        >
          {submitting ? t('common.loading') : t('withdraw.unbindSubmit')}
        </button>
      </form>
    </div>
  );
}
